(() => {
  if (window.UnionLanguage) return;
  const entries = window.UnionLanguageData || [];
  const normalize = value => value.replace(/\s+/g, ' ').trim();
  const dictionary = new Map();
  for (const entry of entries) for (const key of [entry.source, entry.es, entry.en]) {
    if (key) dictionary.set(normalize(key), entry);
  }
  const blocked = 'script,style,noscript,textarea,code,pre,[translate="no"],[data-shopify-editor]';
  const records = new WeakMap();
  const attributes = ['placeholder', 'aria-label', 'title', 'alt'];
  const documentTitleSource = document.title;
  const metadataRecords = Array.from(document.querySelectorAll('meta[name="description"],meta[property="og:title"],meta[property="og:description"],meta[name="twitter:title"],meta[name="twitter:description"],meta[property="og:image:alt"],meta[name="twitter:image:alt"]')).map(element => ({ element, source: element.getAttribute('content') || '' }));
  let language = 'en';
  try { language = localStorage.getItem('union-language') === 'es' ? 'es' : 'en'; } catch (_) {}
  function translate(text, lang = language) {
    const normalized = normalize(text);
    const entry = dictionary.get(normalized);
    if (entry) return text.replace(text.trim(), entry[lang]);
    // Prices, cart counts and option labels retain their live values.
    const match = normalized.match(/^(.+?)(\s+·\s+\$[\s\S]+|\s+\(\d+\))$/);
    if (match && dictionary.has(match[1])) return dictionary.get(match[1])[lang] + match[2];
    if (text.includes(' · ')) return text.split(' · ').map(part => dictionary.get(normalize(part))?.[lang] || part).join(' · ');
    if (/^\d+ (formats|formatos)$/.test(normalized)) return normalized.replace(/formats|formatos/, lang === 'es' ? 'formatos' : 'formats');
    const cartCount = normalized.match(/^(?:Total de artículos en el carrito|Total items in cart):\s*(\d+)$/);
    if (cartCount) return `${lang === 'es' ? 'Total de artículos en el carrito' : 'Total items in cart'}: ${cartCount[1]}`;
    const removeMembership = normalized.match(/^(?:Remove|Eliminar) (Aficionado|Tobacconist|Sommelier|Master Blender)(?: - Pack mensual)?$/);
    if (removeMembership) return `${lang === 'es' ? 'Eliminar' : 'Remove'} ${removeMembership[1]}`;
    const removePorto = normalized.match(/^(?:Remove|Eliminar) (Porto Real (?:Connecticut|Classic|Maduro))(?: - (Pack of 3|Box of 10|Box of 20|Paquete de 3|Caja de 10|Caja de 20))?$/);
    if (removePorto) return (lang === 'es' ? 'Eliminar ' : 'Remove ') + removePorto[1] + (removePorto[2] ? ' - ' + translate(removePorto[2], lang) : '');
    return text;
  }
  function nodeText(node) {
    if (!node.parentElement || node.parentElement.closest(blocked) || !node.nodeValue.trim()) return;
    const previous = records.get(node);
    const source = previous && previous.last === node.nodeValue ? previous.source : node.nodeValue;
    const next = translate(source);
    records.set(node, { source, last: next });
    if (node.nodeValue !== next) node.nodeValue = next;
  }
  function elementAttrs(el) {
    if (el.closest(blocked)) return;
    const previous = records.get(el) || {};
    for (const attr of attributes) if (el.hasAttribute(attr)) {
      const current = el.getAttribute(attr);
      const source = previous[attr]?.last === current ? previous[attr].source : current;
      const next = translate(source);
      previous[attr] = { source, last: next };
      if (current !== next) el.setAttribute(attr, next);
    }
    records.set(el, previous);
  }
  function render(root) {
    if (root.nodeType === Node.TEXT_NODE) return nodeText(root);
    if (!(root instanceof Element) || root.closest(blocked)) return;
    elementAttrs(root);
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, {
      acceptNode(node) { return node.nodeType === 1 && node.matches(blocked) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT; }
    });
    let node;
    while ((node = walker.nextNode())) node.nodeType === Node.TEXT_NODE ? nodeText(node) : elementAttrs(node);
  }
  const config = { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: attributes };
  const observer = new MutationObserver(changes => {
    observer.disconnect();
    const roots = new Set();
    for (const change of changes) {
      if (change.type === 'childList') change.addedNodes.forEach(node => roots.add(node));
      else roots.add(change.target);
    }
    for (const root of roots) if (root.isConnected) render(root);
    syncControls();
    observer.observe(document.body, config);
  });
  function syncControls() {
    document.querySelectorAll('[data-union-copy]').forEach(element => {
      const copy = element.dataset[language] || element.dataset.en || element.dataset.es || '';
      if (element.textContent !== copy) element.textContent = copy;
      element.lang = language;
    });
    document.querySelectorAll('[data-union-lang]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.unionLang === language)));
  }
  function setLanguage(next, persist = true) {
    if (!['es', 'en'].includes(next)) return;
    observer.disconnect();
    language = next;
    document.documentElement.lang = next;
    if (persist) try { localStorage.setItem('union-language', next); } catch (_) {}
    render(document.body);
    syncControls();
    const titleParts = documentTitleSource.split(' – ');
    titleParts[0] = translate(titleParts[0], language);
    document.title = titleParts.join(' – ');
    for (const { element, source } of metadataRecords) element.setAttribute('content', translate(source, language));
    observer.observe(document.body, config);
    document.dispatchEvent(new CustomEvent('union:languagechange', { detail: { language } }));
  }
  document.addEventListener('click', event => {
    const button = event.target.closest?.('[data-union-lang]');
    if (button) setLanguage(button.dataset.unionLang);
  });
  window.addEventListener('storage', event => { if (event.key === 'union-language') setLanguage(event.newValue, false); });
  document.addEventListener('shopify:section:load', () => setLanguage(language, false));
  window.addEventListener('pageshow', () => setLanguage(language, false));
  window.addEventListener('pagehide', () => observer.disconnect());
  window.UnionLanguage = { set: setLanguage, translate, get language() { return language; } };
  setLanguage(language, false);
})();
